export function isEqual(value: any[], other: any) {
  if (value === other) {
    return true; // Handle primitive types and reference equality for same objects
  }

  if (value == null || other == null) {
    return false; // Handle null and undefined
  }

  if (typeof value !== typeof other) {
    return false; // Different types are not equal
  }

  if (typeof value === "object") {
    if (Array.isArray(value) !== Array.isArray(other)) {
      return false; // One is an array, the other is not
    }

    if (Array.isArray(value)) {
      if (value.length !== other.length) {
        return false; // Arrays of different lengths are not equal
      }
      for (let i = 0; i < value.length; i++) {
        if (!isEqual(value[i], other[i])) {
          return false; // Recursive check for array elements
        }
      }
      return true;
    }

    // Handle objects
    const valueKeys = Object.keys(value);
    const otherKeys = Object.keys(other);

    if (valueKeys.length !== otherKeys.length) {
      return false; // Objects with different numbers of keys are not equal
    }

    for (const key of valueKeys) {
      if (!otherKeys.includes(key) || !isEqual(value[key], other[key])) {
        return false; // Recursive check for object values
      }
    }

    return true;
  }

  return false; // If it's not an object, return false (this handles functions, symbols, etc.)
}

export interface QuantityItemLike {
  measurement_unit?: string | null;
  request_quantity?: number | string | null;
}

/**
 * Summarize quantities grouped by measurement unit into a display string like "10 cái\n5 kg".
 * - Treats missing units as empty string
 * - Ignores items with falsy quantities
 */
export function summarizeQuantitiesByUnit(items: QuantityItemLike[]): string {
  const totalsByUnit: Record<string, number> = {};
  if (!Array.isArray(items)) return "";

  for (const item of items) {
    const unit = (item?.measurement_unit as string) || "";
    const q = item?.request_quantity as any;
    const quantity = typeof q === "string" ? parseFloat(q) : typeof q === "number" ? q : 0;
    if (!quantity || isNaN(quantity)) continue;
    totalsByUnit[unit] = (totalsByUnit[unit] || 0) + quantity;
  }

  const round = (num: number, decimals: number) => {
    const factor = Math.pow(10, decimals);
    return Math.round((num + Number.EPSILON) * factor) / factor;
  };

  return Object.entries(totalsByUnit)
    .map(([unit, qty]) => {
      const normalizedUnit = (unit || '').trim().toLowerCase();
      const isIntegerUnit = normalizedUnit === 'cái' || normalizedUnit === 'cai';
      const rounded = isIntegerUnit ? Math.round(qty) : round(qty, 2);
      return `${rounded} ${unit}`.trim();
    })
    .join('\n');
}